import NeptuneElement from '../../NeptuneElement.js';

class Menu extends NeptuneElement {

    #currentItemIndex = 0;

    templateMarkup = `
        <menu role="menu">
            <slot name="menu-item"></slot>
        </menu>
    `;

    componentCssUrl = '/src/components/navigation/menu/menu.css';

    /**
     * Creates a menu component instance.
     */
    constructor() {
        super();
    }

    /**
     * Initializes popover behavior and anchor wiring after render.
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        await super.connectedCallback();
        this.setAttribute('popover', 'manual');
        this.triggerElement = this.previousElementSibling;
        this.normalizeMenuItems();
        this.processInitialAttributesAfterRender(Menu.observedAttributes);
    }

    /**
     * Lists attributes that trigger attributeChangedCallback.
     * @returns {string[]}
     */
    static get observedAttributes() {
        return ['open'];
    }

    /**
     * Cleans up anchor-related inline styles when component disconnects.
     * @returns {Promise<void>}
     */
    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    /**
     * Responds to observed attribute changes after render.
     * @param {string} name
     * @param {string|null} oldValue
     * @param {string|null} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Applies behavior tied to supported attributes.
     * @param {string} name
     * @param {string|null} oldValue
     * @param {string|null} newValue
     */
    processAttribute(name, oldValue, newValue) {
        if (!this.shadowRoot) {return;}

        if (name === 'open') {
            const isOpen = this.hasAttribute('open');

            if (isOpen) {
                this.openPopover();
                this.#currentItemIndex = 0;
                const items = this.getFocusableMenuItems();
                const selectedIndex = items.findIndex(item => item.hasAttribute('selected'));
                if (selectedIndex !== -1) {
                    this.#currentItemIndex = selectedIndex;
                    items[selectedIndex].focus();
                } else {
                    items[0]?.focus();
                }
                this.syncTriggerAria(true);
            } else {
                this.closePopover();
                this.syncTriggerAria(false);
            }
        }
    }

    /**
     * Ensures direct children are wrapped as slotted menu items.
     * @returns {void}
     */
    normalizeMenuItems() {
        const children = Array.from(this.children);
        let previousMenuItem = null;

        children.forEach((child) => {
            if (child.tagName === 'SLOT') {
                return;
            }

            if (child.tagName === 'NEPTUNE-MENU-ITEM') {
                child.setAttribute('slot', 'menu-item');
                previousMenuItem = child;
                return;
            }

            if (child.tagName === 'HR') {
                if (previousMenuItem) {
                    previousMenuItem.setAttribute('divider', '');
                }
                child.remove();
                return;
            }

            const wrapper = document.createElement('neptune-menu-item');
            wrapper.setAttribute('slot', 'menu-item');
            this.insertBefore(wrapper, child);

            wrapper.appendChild(child);
            previousMenuItem = wrapper;
        });
    }

    /**
     * Backward-compatible alias to normalize item slot structure.
     * @returns {void}
     */
    addSlotAttributes() {
        this.normalizeMenuItems();
    }

    /**
     * Returns enabled menu items that can receive focus.
     * @returns {HTMLElement[]}
     */
    getFocusableMenuItems() {
        const slot = this.shadowRoot?.querySelector('slot[name="menu-item"]');
        const assigned = slot?.assignedElements({ flatten: true }) ?? [];
        const items = assigned.length > 0 ? assigned : Array.from(this.querySelectorAll('neptune-menu-item'));
        return items.filter(
            item => item.tagName === 'NEPTUNE-MENU-ITEM' &&
                    !item.hasAttribute('disabled') &&
                    item.getAttribute('aria-disabled') !== 'true'
        );
    }

    /**
     * Closes the menu when a click occurs outside menu and trigger.
     * @param {MouseEvent} event
     * @returns {void}
     */
    handleDocumentClick(event) {
        if (!this.hasAttribute('open')) {
            return;
        }

        const clickedMenu = event.composedPath().includes(this);
        const clickedTrigger = this.triggerElement && event.composedPath().includes(this.triggerElement);
        
        if (!clickedMenu && !clickedTrigger) {
            this.removeAttribute('open');
        }
    }

    /**
     * Handles menu keyboard navigation actions.
     * @param {{ detail?: { key?: string, sourceItem?: HTMLElement } }} event
     * @returns {void}
     */
    handleMenuNavigation(event) {
        const items = this.getFocusableMenuItems();
        if (!items.length) {
            return;
        }

        const sourceItem = event.detail?.sourceItem;
        if (sourceItem) {
            const sourceItemIndex = items.indexOf(sourceItem);
            if (sourceItemIndex !== -1) {
                this.#currentItemIndex = sourceItemIndex;
            }
        }

        const key = event.detail?.key;

        switch (key) {
            case 'ArrowDown':
            case 'Down': {
                this.#currentItemIndex = (this.#currentItemIndex + 1) % items.length;
                items[this.#currentItemIndex]?.focus();
                break;
            }
            case 'ArrowUp':
            case 'Up': {
                this.#currentItemIndex = (this.#currentItemIndex - 1 + items.length) % items.length;
                items[this.#currentItemIndex]?.focus();
                break;
            }
            case 'Home': {
                this.#currentItemIndex = 0;
                items[0]?.focus();
                break;
            }
            case 'End': {
                this.#currentItemIndex = items.length - 1;
                items[items.length - 1]?.focus();
                break;
            }
            case 'Escape': {
                this.#currentItemIndex = 0;
                this.removeAttribute('open');
                this.triggerElement?.focus?.();
                break;
            }
            default:
                break;
        }
    }

    /**
     * Opens the host popover when supported.
     * @returns {void}
     */
    openPopover() {
        this.showPopover?.();
    }

    /**
     * Closes the host popover when supported.
     * @returns {void}
     */
    closePopover() {
        this.hidePopover?.();
    }

    /**
     * Updates trigger accessibility attributes to reflect menu state.
     * @param {boolean} isOpen
     * @returns {void}
     */
    syncTriggerAria(isOpen) {
        if (!this.triggerElement) {
            return;
        }

        this.triggerElement.setAttribute('aria-haspopup', 'menu');
        this.triggerElement.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    /**
     * Attaches global listeners used while the component is mounted.
     * @returns {void}
     */
    setEventListeners() {
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleMenuNavigation = this.handleMenuNavigation.bind(this);
        this.handleMenuItemSelect = this.handleMenuItemSelect.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
        document.addEventListener('click', this.handleDocumentClick);
        window.addEventListener('scroll', this.handleScroll, true);
        this.addEventListener('neptune-menu-navigate', this.handleMenuNavigation);
        this.addEventListener('menu-item-select', this.handleMenuItemSelect);
        this.addEventListener('neptune-checkbox-change', this.handleCheckboxChange);
    }

    /**
     * Removes global listeners registered by setEventListeners.
     * @returns {void}
     */
    removeEventListeners() {
        document.removeEventListener('click', this.handleDocumentClick);
        window.removeEventListener('scroll', this.handleScroll, true);
        this.removeEventListener('neptune-menu-navigate', this.handleMenuNavigation);
        this.removeEventListener('menu-item-select', this.handleMenuItemSelect);
        this.removeEventListener('neptune-checkbox-change', this.handleCheckboxChange);
    }

    /**
     * Closes menu on external scroll while ignoring internal menu scrolling.
     * @param {Event} event
     * @returns {void}
     */
    handleScroll(event) {
        if (!this.hasAttribute('open')) {
            return;
        }

        const eventPath = typeof event?.composedPath === 'function' ? event.composedPath() : [];
        const isMenuInternalScroll = eventPath.includes(this) || event?.target === this;
        if (isMenuInternalScroll) {
            return;
        }

        this.removeAttribute('open');
    }

    /**
     * Returns checked neptune-checkbox elements inside the menu.
     * @returns {Element[]}
     */
    getCheckedCheckboxes() {
        return Array.from(this.querySelectorAll('neptune-checkbox[checked]'));
    }

    /**
     * Returns values of checked checkboxes.
     * @returns {(string|null)[]}
     */
    getCheckedValues() {
        return this.getCheckedCheckboxes().map(checkbox => checkbox.getAttribute('value'));
    }

    /**
     * Returns labels of checked checkboxes.
     * @returns {(string|null)[]}
     */
    getCheckedLabels() {
        return this.getCheckedCheckboxes().map(checkbox => checkbox.getAttribute('label'));
    }

    /**
     * Toggles the selected attribute on the clicked menu item.
     * In single-select mode (default) clears other items first.
     * In multiselect mode every item can be toggled independently.
     * @param {CustomEvent} event
     * @returns {void}
     */
    handleMenuItemSelect(event) {
        const item = event.detail?.item;
        if (!item) {return;}

        if (item.hasAttribute('selected')) {
            item.removeAttribute('selected');
            return;
        }

        if (!this.hasAttribute('multiselect')) {
            const slot = this.shadowRoot?.querySelector('slot[name="menu-item"]');
            const allItems = slot?.assignedElements({ flatten: true }) ?? [];
            allItems.forEach(menuItem => menuItem.removeAttribute('selected'));
        }

        item.setAttribute('selected', '');
    }

    /**
     * Syncs the selected attribute on the parent menu-item when a checkbox changes.
     * @param {CustomEvent} event
     * @returns {void}
     */
    handleCheckboxChange(event) {
        const menuItem = event.target?.closest?.('neptune-menu-item');
        if (!menuItem) {return;}

        if (event.detail?.checked) {
            menuItem.setAttribute('selected', '');
        } else {
            menuItem.removeAttribute('selected');
        }
    }
}

customElements.define('neptune-menu', Menu);

export default Menu;